import { AnalyticsTarget } from 'enums/AnalyticsTarget';
import { InputWidgetButtonAction } from 'enums/InputWidgetButtonAction';
import { InputWidgetElementType } from 'enums/InputWidgetElementType';
import { MessageCommandType } from 'enums/MessageCommandType';
import { MessageType } from 'enums/MessageType';
import { SurveyWidget } from 'enums/SurveyWidget';
import { InputWidgetButtonData } from 'interfaces/InputWidgetButtonData';
import { InputWidgetData } from 'interfaces/InputWidgetData';
import { InputWidgetElementData } from 'interfaces/InputWidgetElementData';
import { InputWidgetElementsMap } from 'interfaces/InputWidgetsMap';
import { MasterDetailElementData } from 'interfaces/MasterDetailElementData';
import {
    MasterDetailRequestCommandData,
    MasterDetailResponseCommandData,
    OtpCommandData,
    SurveyRequestCommandData,
} from 'interfaces/MessageData';
import { OtpCodeElementData } from 'interfaces/OtpCodeElementData';
import { DefaultRootState } from 'react-redux';
import { all, call, put, select, take, takeEvery } from 'redux-saga/effects';
import { services } from 'services/container';
import {
    CommandActionType,
    ReceiveCommandAction,
    sendCommand,
} from 'state/commands/commands.actions';
import { sendMessage } from 'state/history/history.actions';
import { createReachGoalActionBy } from 'utils/analytics';

import {
    closeInputWidget,
    InputWidgetActionType,
    InputWidgetReplyButtonPressedAction,
    InputWidgetSendOtpCodeAction,
    resetInputWidgetOtpTimestamp,
    sendInputWidgetReply,
    SendInputWidgetReplyAction,
    sendSurveyWidgetReply,
    SendSurveyWidgetReplyAction,
    setInputWidgetButtonEnabled,
    SetInputWidgetMasterElementValueAction,
    setInputWidgetOtpInProgress,
    updateInputWidgetElement,
    UpdateInputWidgetMasterDetailsAction,
} from './inputWidget.actions';

export const getOkReplyText = (
    widgetsMap: InputWidgetElementsMap,
    buttonData: InputWidgetButtonData,
    elements: InputWidgetElementData[],
) => {
    const elementsDescription = elements.map((el) => widgetsMap[el.type]?.stringifyData(el));

    let buttonDataCaption = buttonData.caption || '';
    buttonDataCaption =
        buttonDataCaption.at(-1) === ' ' ? buttonDataCaption : buttonDataCaption + ' ';

    return buttonDataCaption + elementsDescription.filter(Boolean).join(',\n') || buttonData.text;
};

export const getCloseReplyText = (buttonData: InputWidgetButtonData) =>
    buttonData.caption || buttonData.text || 'Отменить операцию';

export const getEditReplyText = (buttonData: InputWidgetButtonData) =>
    buttonData.caption || buttonData.text || 'Назад';

function* inputWidgetReplyButtonPressedSaga() {
    yield takeEvery(
        InputWidgetActionType.ReplyButtonPressed,
        function* (buttonPressedAction: InputWidgetReplyButtonPressedAction) {
            const action = buttonPressedAction.payload;
            const state: DefaultRootState = yield select();
            const button = state.inputWidget?.buttons?.find((b) => b.action === action);

            if (!button) return;

            switch (action) {
                case InputWidgetButtonAction.Ok:
                case InputWidgetButtonAction.Close:
                    if (state?.inputWidget?.isSurvey) {
                        services.analytics.send(
                            createReachGoalActionBy(AnalyticsTarget.SURVEY_WIDGET_CLOSE),
                        );
                    }
                case InputWidgetButtonAction.Edit:
                    if (state?.inputWidget?.isSurvey) {
                        yield put(sendSurveyWidgetReply(action));
                    } else {
                        yield put(sendInputWidgetReply(action));
                    }
                    break;

                case InputWidgetButtonAction.Retry:
                    yield put(setInputWidgetOtpInProgress(true));
                    yield put(sendCommand({ type: MessageCommandType.GenerateNewOTPReq }));

                    const otpElement = state.inputWidget?.elements?.find(
                        (e) => e.type === InputWidgetElementType.OtpCodeV2,
                    ) as OtpCodeElementData;

                    if (otpElement) {
                        yield put(
                            updateInputWidgetElement({
                                elementId: otpElement.elementId,
                                elementData: {
                                    ...otpElement,
                                    alertText: undefined,
                                },
                            }),
                        );
                    }

                    while (true) {
                        const cmdAction: ReceiveCommandAction = yield take(
                            CommandActionType.Receive,
                        );

                        const command = cmdAction.payload as OtpCommandData;
                        const { type, data } = command;

                        if (type === MessageCommandType.GenerateNewOTPResp) {
                            if (data.result === 'success')
                                yield put(resetInputWidgetOtpTimestamp());

                            yield put(setInputWidgetOtpInProgress(false));

                            break;
                        }
                    }
            }
        },
    );
}

function* inputWidgetCloseSaga() {
    yield takeEvery(InputWidgetActionType.Close, function* () {});
}

function* inputWidgetReplySaga() {
    yield takeEvery(
        InputWidgetActionType.SendReply,
        function* (replyAction: SendInputWidgetReplyAction) {
            const widgetData: InputWidgetData = yield select(
                (state: DefaultRootState) => state.inputWidget,
            );

            if (!widgetData) return;

            // Import input widgets chunk asynchronously
            const widgetsMap: InputWidgetElementsMap = yield call(() =>
                import(
                    /* webpackChunkName: "input-widgets" */ 'components/Chat/ChatFooter/InputWidget/_elements/WidgetElementMap'
                ).then((m) => m.default),
            );

            const { elements, buttons } = widgetData;
            const { action } = replyAction.payload;

            const button = buttons.find((button) => button.action === action);

            let replyText = 'Продолжить';

            switch (action) {
                case InputWidgetButtonAction.Ok:
                    replyText = getOkReplyText(widgetsMap, button, elements);
                    break;

                case InputWidgetButtonAction.Close:
                    replyText = getCloseReplyText(button);
                    break;

                case InputWidgetButtonAction.Edit:
                    replyText = getEditReplyText(button);
                    break;
            }

            const elementsReplyData = yield call(() =>
                Promise.all(elements.map((el) => widgetsMap[el.type]?.stripData(el) ?? {})),
            );

            yield put(
                sendMessage({
                    type: MessageType.Text,
                    text: replyText,
                    metadata: {
                        text_chat_widget: {
                            elements: elementsReplyData,
                            button: action,
                        },
                    },
                }),
            );

            if (button.analytics) {
                services.analytics.sendAnalyticsAction(
                    button.text,
                    'Web-виджет. Клик по кнопке виджета сценария',
                );
            }
        },
    );
}

function* surveyWidgetReplySaga() {
    yield takeEvery(
        InputWidgetActionType.SendSurveyReply,
        function* (replyAction: SendSurveyWidgetReplyAction) {
            const state: InputWidgetData = yield select(
                (state: DefaultRootState) => state.inputWidget,
            );

            const survey = state.elements.find((el) => !!el.survey);
            const { value, type } = survey;

            const { action } = replyAction.payload;

            const isOk = action === InputWidgetButtonAction.Ok;

            if (!action) return;

            const replyText = survey?.survey?.answerText ?? 'surveyReply';

            yield put(
                sendMessage({
                    type: MessageType.Tech,
                    text: replyText,
                    metadata: {
                        command: {
                            type: MessageCommandType.SurveyReply,
                            survey: {
                                type: type as SurveyWidget,
                                pressedButton: action,
                                ...(isOk ? (value as SurveyRequestCommandData['survey']) : {}),
                            },
                        },
                    },
                }),
            );

            const isSurveyEnd = state.elements.find(
                (element) => element.survey.type === SurveyWidget.COMPLETESCREEN,
            );
            const isClose = action === InputWidgetButtonAction.Close;

            if (isSurveyEnd || isClose) {
                yield put(closeInputWidget());
            }

            if (isOk) yield put(setInputWidgetButtonEnabled(InputWidgetButtonAction.Ok, false));
        },
    );
}

function* sendOtpCodeSaga() {
    yield takeEvery(
        InputWidgetActionType.SendOtpCode,
        function* (inputAction: InputWidgetSendOtpCodeAction) {
            const code = inputAction.payload;

            yield put(setInputWidgetOtpInProgress(true));

            yield put(
                sendCommand({ type: MessageCommandType.OTPCodeV2SendCode, data: { text: code } }),
            );

            while (true) {
                const cmdAction: ReceiveCommandAction = yield take(CommandActionType.Receive);

                const command = cmdAction.payload;
                const { type, data } = command;

                if (type === MessageCommandType.OTPCodeV2FailureCode) {
                    yield put(setInputWidgetOtpInProgress(false));

                    const state: DefaultRootState = yield select();
                    const otpElement = state.inputWidget?.elements?.find(
                        (e) => e.type === InputWidgetElementType.OtpCodeV2,
                    ) as OtpCodeElementData;

                    if (otpElement) {
                        yield put(
                            updateInputWidgetElement({
                                elementId: otpElement.elementId,
                                elementData: {
                                    ...otpElement,
                                    alertText: data.text,
                                },
                            }),
                        );
                    }

                    break;
                }

                if (type === MessageCommandType.OTPCodeV2HideWidget) {
                    yield put(closeInputWidget());
                    break;
                }
            }
        },
    );
}

function* updateMasterDetailsSaga() {
    yield takeEvery(
        InputWidgetActionType.UpdateMasterDetails,
        function* (updateAction: UpdateInputWidgetMasterDetailsAction) {
            const state: DefaultRootState = yield select();
            const masterDetail = state.inputWidget?.elements?.find(
                (el) => el.type === InputWidgetElementType.MasterDetail,
            ) as MasterDetailElementData;

            if (!masterDetail) return;

            const { items, clientSide } = updateAction.payload;

            yield put(
                updateInputWidgetElement({
                    elementId: masterDetail.elementId,
                    elementData: {
                        ...masterDetail,
                        items: masterDetail.items.map((itm) => ({
                            ...itm,
                            value: items.find((i) => i.id === itm.id).value,
                        })),
                        disabled: !clientSide,
                    },
                }),
            );

            if (clientSide) return;

            yield put(
                sendCommand({
                    type: MessageCommandType.MasterDetailRequest,
                    items,
                } as MasterDetailRequestCommandData),
            );

            while (true) {
                const cmdAction: ReceiveCommandAction = yield take(CommandActionType.Receive);

                const command = cmdAction.payload as MasterDetailResponseCommandData;
                const { type, headerItems, items } = command;

                if (type === MessageCommandType.MasterDetailResponse) {
                    const state: DefaultRootState = yield select();
                    const masterDetail = state.inputWidget?.elements?.find(
                        (el) => el.type === InputWidgetElementType.MasterDetail,
                    ) as MasterDetailElementData;

                    if (!masterDetail) break;

                    yield put(
                        updateInputWidgetElement({
                            elementId: masterDetail.elementId,
                            elementData: {
                                ...masterDetail,
                                headerItems,
                                items,
                                disabled: false,
                            },
                        }),
                    );

                    break;
                }
            }
        },
    );
}

function* masterElementValueUpdatedSaga() {
    yield takeEvery(
        InputWidgetActionType.SetMasterElementValue,
        function* ({ payload: { elementId, value } }: SetInputWidgetMasterElementValueAction) {
            const state: DefaultRootState = yield select();

            const slaveElements = state.inputWidget?.dynamicElements?.filter(
                (el) => el.parentId === elementId,
            );

            if (!slaveElements) return;

            for (const slaveElement of slaveElements) {
                if (!slaveElement.options || Object.keys(slaveElement.options).length === 0)
                    continue;
                const option = slaveElement.options[value] || slaveElement.options['else'];

                if (!option) continue;

                const dynamicElementId = slaveElement.elementId;

                yield put(
                    updateInputWidgetElement({
                        elementId: dynamicElementId,
                        elementData: {
                            ...option,
                            dynamicElementId,
                        },
                    }),
                );
            }
        },
    );
}

export function* inputWidgetSaga() {
    yield all([
        inputWidgetReplyButtonPressedSaga(),
        inputWidgetReplySaga(),
        inputWidgetCloseSaga(),
        sendOtpCodeSaga(),
        updateMasterDetailsSaga(),
        masterElementValueUpdatedSaga(),
        surveyWidgetReplySaga(),
    ]);
}
